/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect } from 'react'
import { Link, useOutletContext, useParams, useNavigate } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import Dropdown from 'react-bootstrap/Dropdown'
import { Nav, NavItem } from 'react-bootstrap'
import Table from 'react-bootstrap/Table'

import AccessDeniedElement from '../../../components/AccessDeniedElement.jsx'
import AdministrationTabs from '../components/AdministrationTabs.jsx'
import ConfirmationModal from '../../../components/ConfirmationModal.jsx'
import CopyRowAsJSON from '../../../components/CopyRowAsJSON.jsx'
import Icon from '../../../components/Icon.jsx'
import Loadable from '../../../components/Loadable.jsx'
import LoadingAnimation from '../../../components/LoadingAnimation.jsx'
import NewGroupModal from './components/NewGroupModal.jsx'
import PageTitle from '../../../components/PageTitle.jsx'
import SelectParentModal from './components/SelectParentModal.jsx'
import TimestampWithUTCTooltip from '../../../components/TimestampWithUTCTooltip.jsx'

import { fetchGroupList, deleteGroup } from '../../../state/actions/groupActions.js'
import { addNotification, NotificationTypes } from '../../../state/actions/notificationActions.js'

import { currentBusinessEntitySignal, businessEntitiesLoadedSignal, businessEntitiesErrorSignal, rootBusinessEntitySignal, businessEntityDictionarySignal } from '../../../state/signals/businessEntitySignals.js'
import { groupListSignal, groupListLoadedSignal, groupListErrorSignal, groupErrorSignal } from '../../../state/signals/groupSignals.js'

import { SortDirectionType } from '../../../utils/constants.js'
import { computeSortedData } from '../../../utils/table.js'

const GroupsPage = function () {

	const navigate = useNavigate()
	const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData } = useOutletContext()

	useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewGroups) {
            addNotification('You are not authorized to view groups for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])
		
	const canEditComputed = useComputed(
		() => {
			return userPermissionsSummary?.value?.canEditGroups
		}
	)

	const { beId } = useParams()

	const selectedGroupSignal = useSignal(null)
	const selectedParentBusinessEntityIdSignal = useSignal(null)
	const showModalNewGroupSignal = useSignal(false)
	const showModalSelectParentSignal = useSignal(false)
	const showModalDeleteGroupSignal = useSignal(false)
	const sortColumnSignal = useSignal(null)
	const sortDirectionSignal = useSignal(SortDirectionType.ASCENDING)

	useEffect(() => {
		fetchGroupList(beId, null, config, accessToken)
	}, [beId, config, accessToken])

	const enrichedGroupListSignal = useComputed(() =>
		groupListSignal.value.map(g => ({
			...g,
			parentName: businessEntityDictionarySignal.value?.[g.parentBusinessEntityId]?.name ?? '',
		}))
	)

	const sortedGroupsSignal = useComputed(computeSortedData(enrichedGroupListSignal, sortColumnSignal, sortDirectionSignal))

	const handleHeaderTableClick = datafield => {
		// if already sorted on this column then toggle sort direction
		if (datafield == sortColumnSignal.value) {
			sortDirectionSignal.value = sortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
			return
		}

		// update sort column - the computed functions will then run and render the sorted data
		sortColumnSignal.value = datafield
	}

	function TableColumnHeader({ title, sortable, datafield }) {
		// non-sortable columns do not have click handlers
		if (!sortable) {
			return (
				<th>{title}</th>
			)
		}

		// sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
		if (datafield != sortColumnSignal.value) {
			return (
				<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
			)
		}

		// datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
		if (sortDirectionSignal.value == SortDirectionType.DESCENDING) {
			return (
				<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
					<Icon icon="caret-down" width="16" height="16"></Icon>
				</th>
			)
		}

		// else must be ascending
		return (
			<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
				<Icon icon="caret-up" width="16" height="16"></Icon>
			</th>
		)
	}

	const showModalDeleteGroup = function (group) {
		selectedGroupSignal.value = group
		showModalDeleteGroupSignal.value = true
	}

	const confirmDeleteGroup = () => {
		//console.log("Confirmed group deletion");
		deleteGroup(
			beId,
			selectedGroupSignal.value.id,
			config,
			accessToken,
		).then(
			() => {
				addNotification(`Deleted group ${selectedGroupSignal.value.name}`, NotificationTypes.SUCCESS, false, "Deletion successful")
			}
		).catch(
			(error) => {
				addNotification(`Failed to delete group ${selectedGroupSignal.value.name}: `.concat(error.toString()), NotificationTypes.ERROR, false, "Deletion failed")
			}
		)
		closeModalDeleteGroup()
	}

	const GroupTableRows = function ({ groups }) {
		if (!groupListLoadedSignal.value) {
			return <tr key="loading"><td colSpan={7}><LoadingAnimation /></td></tr>
		}
		if (groups.length == 0) {
			return (
				<tr>
					<td colSpan={7} className='empty-record'>No Groups to display.</td>
				</tr>
			)
		}
		let rows = []
		for (let i = 0; i < groups.length; i++) {
			const group = groups[i]
			//console.log(group)
			rows.push(
				<tr key={group.id}>
					<td>
						<Dropdown>
							<Dropdown.Toggle variant="link" size="sm" id="actions">
								<Icon icon="gear" width={16} height={16} />
							</Dropdown.Toggle>
							<Dropdown.Menu>
								<CopyRowAsJSON item={group} field="name" />
								{
									canEditComputed.value && 
											(group.merchantCount + group.roleCount + group.userCount == 0) &&
												<Dropdown.Item onClick={() => { showModalDeleteGroup(group) }}>
													<Icon icon='trash' height={16} weight={16} />&nbsp;Delete Group
												</Dropdown.Item>
								}
							</Dropdown.Menu>
						</Dropdown>
					</td>
					<td className='breakWordWrap'><Link to={`/be/${beId}/management/groups/${group.id}`}>{group.name}</Link></td>
					<td>{group.parentName}</td>
					<td className='visible-md visible-lg'>{group.merchantCount}</td>
					<td className='visible-md visible-lg'>{group.roleCount}</td>
					<td className='visible-md visible-lg'>{group.userCount}</td>
					<td className='hidden-xs'>
						<TimestampWithUTCTooltip>{group.lastUpdatedAt}</TimestampWithUTCTooltip>
					</td>
				</tr>
			)
		}
		return rows
	}

	const closeModalDeleteGroup = function () {
		showModalDeleteGroupSignal.value = false
	}

	const showModalNewGroup = function () {
		//console.log("Showing new group modal")
		selectedParentBusinessEntityIdSignal.value = currentBusinessEntitySignal.value.id
		selectedGroupSignal.value = {
			parentBusinessEntityId: currentBusinessEntitySignal.value.id
		}
		showModalNewGroupSignal.value = true
	}

	const closeModalNewGroup = function () {
		showModalNewGroupSignal.value = false
	}

	const parentBusinessEntityComputed = useComputed(
		() => {
			const f = "parentBusinessEntityComputed()"
			if (!businessEntitiesLoadedSignal.value) {
				return null
			}
			if (!groupListLoadedSignal.value) {
				return null
			}
			const group = selectedGroupSignal.value
			if (!("parentBusinessEntityId" in group)) {
				throw Error(`${f}: Group does not know its parent business entity ID`)
			}
			const parentId = group.parentBusinessEntityId
			if (!businessEntitiesLoadedSignal.value) {
				throw Error("Business entities were not loaded")
			}
			if (!businessEntityDictionarySignal.value) {
				throw Error("Business entity dictionary was not populated")
			}
			if (!(parentId in businessEntityDictionarySignal.value)) {
				throw Error(`${f}: Business entity with ID ${parentId} is absent from the dictionary`)
			}
			return businessEntityDictionarySignal.value[parentId]
		}
	)

	return (
		<>
			<Breadcrumb className="px-3 pt-1 bg-light">
				<Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/accounts` }}>Administration</Breadcrumb.Item>
				<Breadcrumb.Item active>Groups</Breadcrumb.Item>
			</Breadcrumb>
			<PageTitle title='Groups' canEditSignal={canEditComputed} />
			<AdministrationTabs activeKey="2"></AdministrationTabs>
			{
				userPermissionsSummary.value && userPermissionsSummary.value.canViewGroups ? (
					<>
						<Nav className="justify-content-between px-3 py-1 my-2 bg-light">                
							<NavItem className="me-auto">
								<Loadable
									loadedSignals={[businessEntitiesLoadedSignal]}
									errorSignals={[businessEntitiesErrorSignal]}
								>
									<div>{currentBusinessEntitySignal.value?.name}&nbsp;- Groups</div>
								</Loadable>
							</NavItem>
							{/* <NavItem>
								{userPermissionsSummary.value && userPermissionsSummary.value.canEditGroups && (
									<Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={showModalNewGroup}>
										<Icon icon="plus-circle" height={16} width={16}></Icon>&nbsp;Create Group
									</Button>
								)}
							</NavItem> */}
						</Nav>

						<div className="panel-body">
							<Table className="table table-responsive table-striped table-hover">
								<thead>
									<tr>
										<TableColumnHeader title="Action" sortable={false} />
										<TableColumnHeader title={'Name'} sortable={true} datafield={'name'}></TableColumnHeader>
										<TableColumnHeader title={"Parent"} sortable={true} datafield={'parentName'}></TableColumnHeader>
										<TableColumnHeader title={'Accounts'} sortable={true} datafield={'merchantCount'}></TableColumnHeader>
										<TableColumnHeader title={'Roles'} sortable={true} datafield={'roleCount'}></TableColumnHeader>
										<TableColumnHeader title={'Users'} sortable={true} datafield={'userCount'}></TableColumnHeader>
										<TableColumnHeader title={'Last Updated'} sortable={true} datafield={'lastUpdatedAt'}></TableColumnHeader>
									</tr>
								</thead>
								<tbody>
									<GroupTableRows groups={sortedGroupsSignal.value}></GroupTableRows>
								</tbody>
							</Table>
						</div>
						{
							userPermissionsSummary.value?.canEditGroups && businessEntitiesLoadedSignal.value && (
								<>
									<NewGroupModal
										showSignal={showModalNewGroupSignal}
										cancelAdd={closeModalNewGroup}
										selectedParentBusinessEntityIdSignal={selectedParentBusinessEntityIdSignal}
										showModalSelectParentSignal={showModalSelectParentSignal}
									></NewGroupModal>
									{
										<SelectParentModal
											showSignal={showModalSelectParentSignal}
											rootBusinessEntity={rootBusinessEntitySignal.value}
											parentBusinessEntityIdSignal={selectedParentBusinessEntityIdSignal}
											previousModalShowSignal={showModalNewGroupSignal}
										></SelectParentModal>
									}
								</>
							)
						}
						<ConfirmationModal
							show={showModalDeleteGroupSignal.value}
							handleCancel={closeModalDeleteGroup}
							handleConfirm={confirmDeleteGroup}
							message={'Are you sure you want to permanently delete this group?'}
							title={'Confirm Delete'}
							confirmText={'Yes'}
							cancelText={'No'}
						></ConfirmationModal>
					</>
				) : (
					<AccessDeniedElement></AccessDeniedElement>
				)
			}
		</>
	)
}
export default GroupsPage